/**
 * @file User / material analysis UI — pages/Dashboard/User/MaterialAnalysisInput/components/SubmissionsDataTable.tsx.
 */

import React, { useState } from "react";
import { MaterialAnalysisSubmission, formatMaterialAnalysisSubmittedOn } from "../../../../../services/materialAnalysisService";
import { formatMaterialAnalysisGeographicalLabel } from "../../../../../utils/backgroundGeographicalSpecificity";
import type { DropdownOption } from "../../../../../interfaces/dropdownInterfaces";
import {
  formatReliabilityDropdownLabel,
  formatSubmissionDataReviewLabel,
} from "../utils/reliabilityFieldLabels";
import {
  getVisibleResultTable,
  shouldShowResultTableSection,
} from "../utils/getVisibleResultTable";
import AnalysisBanner from "../../../../../components/common/AnalysisBanner";
import Button from "../../../../../components/common/Button";
import "./SubmissionsDataTable.css";

interface SubmissionsDataTableProps {
  materialSubmissions: MaterialAnalysisSubmission[];
  reliableSubmissions: MaterialAnalysisSubmission[];
  dataSpecificitySubmissions: MaterialAnalysisSubmission[];
  dataSpecificityApplied?: boolean;
  loading?: boolean;
  hasData?: boolean;
  materialSelected?: boolean;
  displaySubmissions?: MaterialAnalysisSubmission[];
  onDownloadPdf?: () => void;
  downloadingPdf?: boolean;
  onDownloadExcel?: () => void;
  downloadingExcel?: boolean;
  dataReviewOptions?: DropdownOption[];
  dataCompletenessOptions?: DropdownOption[];
  dataAcquisitionOptions?: DropdownOption[];
}

const getGeographicalLabel = (s: MaterialAnalysisSubmission): string =>
  formatMaterialAnalysisGeographicalLabel(s);

const pickCarbonSummary = (s: MaterialAnalysisSubmission) =>
  s.a1a3Summary || s.a3Summary || s.a2Summary || s.a1Summary || null;

const formatEmbodiedCarbonNumber = (
  summary: { embodiedCarbonValue?: number } | null | undefined
) => {
  if (!summary) return "-";
  const value = summary.embodiedCarbonValue;
  if (value === undefined || value === null) return "-";
  const numValue = typeof value === "number" ? value : Number(value);
  if (isNaN(numValue)) return "-";
  return numValue.toFixed(2);
};

interface SubmissionListSectionProps {
  title: string;
  submissions: MaterialAnalysisSubmission[];
  defaultExpanded?: boolean;
  dataReviewOptions?: DropdownOption[];
  dataCompletenessOptions?: DropdownOption[];
  dataAcquisitionOptions?: DropdownOption[];
}

const SubmissionListSection: React.FC<SubmissionListSectionProps> = ({
  title,
  submissions,
  defaultExpanded = true,
  dataReviewOptions = [],
  dataCompletenessOptions = [],
  dataAcquisitionOptions = [],
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const itemsPerPage = 10;

  const totalPages = Math.ceil(submissions.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedData = submissions.slice(startIndex, endIndex);

  if (submissions.length === 0) {
    return (
      <section className="analysis-card submissions-table-card submissions-list-section submissions-list-section--collapsible">
        <header
          className="analysis-card__header submissions-list-section__header"
          onClick={() => setIsExpanded((e) => !e)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === "Enter" && setIsExpanded((exp) => !exp)}
          aria-expanded={isExpanded}
        >
          <div className="submissions-list-section__header-left">
            <span className="submissions-list-section__chevron" aria-hidden>
              {isExpanded ? "▼" : "▶"}
            </span>
            <h3>{title}</h3>
          </div>
          <p>0 submissions</p>
        </header>
        {isExpanded && (
          <div className="submissions-table-placeholder">
            <p>No submissions in this pool.</p>
          </div>
        )}
      </section>
    );
  }

  return (
    <section className="analysis-card submissions-table-card submissions-list-section submissions-list-section--collapsible">
      <header
        className="analysis-card__header submissions-list-section__header"
        onClick={() => setIsExpanded((e) => !e)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === "Enter" && setIsExpanded((exp) => !exp)}
        aria-expanded={isExpanded}
      >
        <div className="submissions-list-section__header-left">
          <span className="submissions-list-section__chevron" aria-hidden>
            {isExpanded ? "▼" : "▶"}
          </span>
          <h3>{title}</h3>
        </div>
        <p>
          Total: {submissions.length} submission{submissions.length !== 1 ? "s" : ""}
        </p>
      </header>

      {isExpanded && (
        <>
          <div className="submissions-table-wrapper">
        <table className="submissions-table">
          <thead>
            <tr>
              <th>Dataset ID</th>
              <th>Submitted on</th>
              <th>Material</th>
              <th>Material sub-type</th>
              <th>Data review</th>
              <th>Data completeness</th>
              <th>Data acquisition</th>
              <th>Temporal representativeness</th>
              <th>Geographical representativeness</th>
              <th>Technological representativeness</th>
              <th>Manufacturer representativeness</th>
              <th>
                Cradle to gate embodied carbon value [kg CO<sub>2</sub>eq/kg]
              </th>
            </tr>
          </thead>
          <tbody>
            {paginatedData.map((submission) => (
              <tr key={submission.submissionId}>
                <td>{submission.submissionCode}</td>
                <td>{formatMaterialAnalysisSubmittedOn(submission)}</td>
                <td>{submission.materialName}</td>
                <td>{submission.subMaterialName || "-"}</td>
                <td>{formatSubmissionDataReviewLabel(submission.dataReview, dataReviewOptions)}</td>
                <td>
                  {formatReliabilityDropdownLabel(
                    dataCompletenessOptions,
                    submission.dataCompleteness
                  )}
                </td>
                <td>
                  {formatReliabilityDropdownLabel(
                    dataAcquisitionOptions,
                    submission.dataAcquisition
                  )}
                </td>
                <td>
                  {submission.startYear ?? "-"} - {submission.endYear ?? "-"}
                </td>
                <td>{getGeographicalLabel(submission)}</td>
                <td>{submission.technologicalSpecificity?.name ?? "-"}</td>
                <td>{submission.manufacturerSpecificity?.name ?? "-"}</td>
                <td>{formatEmbodiedCarbonNumber(pickCarbonSummary(submission))}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

          {totalPages > 1 && (
            <div className="submissions-pagination">
              <div className="pagination-info">
                <span>
                  Showing {startIndex + 1} to {Math.min(endIndex, submissions.length)} of{" "}
                  {submissions.length} entries
                </span>
              </div>
              <div className="pagination-controls">
                <button
                  className="pagination-btn"
                  onClick={(ev) => {
                    ev.stopPropagation();
                    setCurrentPage((p) => Math.max(1, p - 1));
                  }}
                  disabled={currentPage === 1}
                >
                  Previous
                </button>
                <div className="pagination-numbers">
                  {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
                    let pageNum: number;
                    if (totalPages <= 7) {
                      pageNum = i + 1;
                    } else if (currentPage <= 4) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 3) {
                      pageNum = totalPages - 6 + i;
                    } else {
                      pageNum = currentPage - 3 + i;
                    }
                    return (
                      <button
                        key={pageNum}
                        className={`pagination-number ${currentPage === pageNum ? "active" : ""}`}
                        onClick={(ev) => {
                          ev.stopPropagation();
                          setCurrentPage(pageNum);
                        }}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>
                <button
                  className="pagination-btn"
                  onClick={(ev) => {
                    ev.stopPropagation();
                    setCurrentPage((p) => Math.min(totalPages, p + 1));
                  }}
                  disabled={currentPage === totalPages}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </section>
  );
};

/** Exported const — Submissions Data Table. */
export const SubmissionsDataTable: React.FC<SubmissionsDataTableProps> = ({
  materialSubmissions,
  reliableSubmissions,
  dataSpecificitySubmissions,
  dataSpecificityApplied = false,
  loading = false,
  hasData = false,
  materialSelected = false,
  displaySubmissions = [],
  onDownloadPdf,
  downloadingPdf = false,
  onDownloadExcel,
  downloadingExcel = false,
  dataReviewOptions = [],
  dataCompletenessOptions = [],
  dataAcquisitionOptions = [],
}) => {
  const handleDownload = () => {
    onDownloadPdf?.();
  };

  const handleDownloadExcel = () => {
    onDownloadExcel?.();
  };

  const visibleResultTable = getVisibleResultTable({
    dataSpecificityApplied,
    materialSubmissions,
    reliableSubmissions,
    dataSpecificitySubmissions,
  });

  const canDownload = (displaySubmissions?.length ?? 0) > 0;

  const showDataSpecificity = visibleResultTable?.pool === "specific";
  const showReliable = visibleResultTable?.pool === "reliable";
  const showMaterial = visibleResultTable?.pool === "total";
  const hasAnyTableData = shouldShowResultTableSection(
    visibleResultTable,
    dataSpecificityApplied
  );

  if (!loading && !hasAnyTableData) {
    return null;
  }

  const content = loading ? (
    <div className="submissions-table-loading">Loading submissions...</div>
  ) : (
    <div className="submissions-lists-wrapper">
      {showDataSpecificity && (
        <SubmissionListSection
          title="Your Specific Data Pool"
          submissions={dataSpecificitySubmissions}
          defaultExpanded={true}
          dataReviewOptions={dataReviewOptions}
          dataCompletenessOptions={dataCompletenessOptions}
          dataAcquisitionOptions={dataAcquisitionOptions}
        />
      )}
      {showReliable && (
        <SubmissionListSection
          title="Reliable Data Pool"
          submissions={reliableSubmissions}
          defaultExpanded={true}
          dataReviewOptions={dataReviewOptions}
          dataCompletenessOptions={dataCompletenessOptions}
          dataAcquisitionOptions={dataAcquisitionOptions}
        />
      )}
      {showMaterial && (
        <SubmissionListSection
          title="Total Data Pool"
          submissions={materialSubmissions}
          defaultExpanded={true}
          dataReviewOptions={dataReviewOptions}
          dataCompletenessOptions={dataCompletenessOptions}
          dataAcquisitionOptions={dataAcquisitionOptions}
        />
      )}
      <div className="submissions-download">
        <Button
          label={downloadingPdf ? "Generating PDF..." : "Download Result"}
          onClick={handleDownload}
          type="button"
          variant="secondary"
          disabled={downloadingPdf || !onDownloadPdf || !canDownload}
        />
        <Button
          label={downloadingExcel ? "Generating Excel..." : "Download Excel"}
          onClick={handleDownloadExcel}
          type="button"
          variant="secondary"
          disabled={downloadingExcel || !onDownloadExcel || !canDownload}
        />
      </div>
    </div>
  );

  return (
    <div className="submissions-table-container">
      <div className="submissions-results-banner">
        <AnalysisBanner title="ICoMEC Dashboard Result" />
      </div>
      {content}
    </div>
  );
};
